import React, { useMemo, useState } from "react";
import { D, st, fmt } from "../../constants";
import { Overlay, MHdr } from "../shared";
import LedControllerTool from "./LedControllerTool";
import LedBidTool from "./LedBidTool";

export default function LedJobEstimate({ clients = [], estimates = [], apiSaveEstimate, openInvoiceModal, setPage, setNavigateTo }) {
  const [controllerCalc, setControllerCalc] = useState({ materialCost: 0, laborCost: 0, total: 0 });
  const [bidCalc, setBidCalc] = useState({ materialsSell: 0, laborCost: 0, total: 0 });
  const [clientId, setClientId] = useState(clients[0]?.id ?? "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [depositModal, setDepositModal] = useState(false);
  const [deposit, setDeposit] = useState({ required: true, type: "percent", amount: "50" });

  const totals = useMemo(() => {
    const laborCost = (controllerCalc.laborCost || 0) + (bidCalc.laborCost || 0);
    const materialsCost = (controllerCalc.materialCost || 0) + (bidCalc.materialsSell || 0);
    return { laborCost, materialsCost, grandTotal: laborCost + materialsCost };
  }, [controllerCalc, bidCalc]);

  const buildItems = () => [
    { desc: "Labor", qty: 1, unit: "job", rate: Math.round(totals.laborCost * 100) / 100 },
    { desc: "Materials", qty: 1, unit: "lot", rate: Math.round(totals.materialsCost * 100) / 100 },
  ];

  const nextEstimateNumber = () =>
    `EST-${String(Math.max(0, ...estimates.map((e) => parseInt(e.number?.replace("EST-", "") || 0))) + 1).padStart(3, "0")}`;

  const doPushToEstimate = async (dep) => {
    setSaving(true);
    setMessage(null);
    try {
      const created = await apiSaveEstimate({
        id: null,
        clientId,
        number: nextEstimateNumber(),
        date: new Date().toISOString().slice(0, 10),
        status: "Draft",
        notes: "LED Strip Lighting",
        items: buildItems(),
        depositRequired: !!dep?.required,
        depositAmount: dep?.required ? (dep.amount || "") : "",
        depositType: dep?.type || "percent",
      });
      setMessage({ ok: true, text: "Estimate created — opening it now." });
      if (created?.id && setPage && setNavigateTo) {
        setPage("bids");
        setNavigateTo({ type: "estimate", id: created.id });
      }
    } catch (e) {
      setMessage({ ok: false, text: "Error: " + e.message });
    }
    setSaving(false);
  };

  const pushToEstimate = () => {
    if (!clientId) { setMessage({ ok: false, text: "Select a client first." }); return; }
    setDepositModal(true);
  };

  const pushToInvoice = () => {
    if (!clientId) { setMessage({ ok: false, text: "Select a client first." }); return; }
    openInvoiceModal(null, null, buildItems(), clientId);
  };

  return (
    <div>
      <div style={st.card}>
        <div style={st.h2}>Send to Client</div>
        <div style={{ fontSize: 13, color: D.muted, marginBottom: 16 }}>
          Combines the Controller and Bid Tool totals below into Labor and Materials lines and pushes them to a real estimate or invoice.
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ display: "block", minWidth: 220 }}>
            <span style={st.label}>Client</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={st.input}>
              <option value="">— Select client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <div style={{ fontSize: 12, color: D.muted }}>
            Labor: <strong style={{ color: D.text }}>{fmt(totals.laborCost)}</strong>
            {"  ·  "}
            Materials: <strong style={{ color: D.text }}>{fmt(totals.materialsCost)}</strong>
            {"  ·  "}
            Total: <strong style={{ color: D.accent }}>{fmt(totals.grandTotal)}</strong>
          </div>
          <button style={st.btn("primary")} disabled={saving} onClick={pushToEstimate}>
            {saving ? "Saving…" : "Push to Estimate"}
          </button>
          <button style={st.btn()} onClick={pushToInvoice}>
            Push to Invoice
          </button>
        </div>
        {message && (
          <div style={{ marginTop: 10, fontSize: 13, color: message.ok ? D.success : D.danger }}>
            {message.text}
          </div>
        )}
      </div>

      <LedControllerTool onCalcChange={setControllerCalc} />
      <LedBidTool controllerTotal={controllerCalc.total} onCalcChange={setBidCalc} />

      {depositModal && (
        <Overlay>
          <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 12, padding: 24, width: "min(420px,95vw)" }}>
            <MHdr title="Convert to Estimate" onClose={() => setDepositModal(false)} />
            <div style={{ fontSize: 13, color: D.muted, marginBottom: 14 }}>Require a deposit before this estimate can be approved?</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={deposit.required} onChange={(e) => setDeposit((d) => ({ ...d, required: e.target.checked }))} style={{ accentColor: D.accent }} />
              <span>Require deposit</span>
            </label>
            {deposit.required && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <select style={{ ...st.input, width: 110 }} value={deposit.type} onChange={(e) => setDeposit((d) => ({ ...d, type: e.target.value }))}>
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed $</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step={deposit.type === "percent" ? "1" : "0.01"}
                  style={{ ...st.input, width: 100 }}
                  value={deposit.amount}
                  onChange={(e) => setDeposit((d) => ({ ...d, amount: e.target.value }))}
                  placeholder={deposit.type === "percent" ? "%" : "$"}
                />
                {deposit.type === "percent" && (
                  <span style={{ fontSize: 12, color: D.success, fontWeight: 600 }}>
                    {fmt(totals.grandTotal * (parseFloat(deposit.amount) || 0) / 100)} due upfront
                  </span>
                )}
                {deposit.type === "fixed" && (
                  <span style={{ fontSize: 12, color: D.success, fontWeight: 600 }}>
                    {fmt(parseFloat(deposit.amount) || 0)} due upfront
                  </span>
                )}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button style={st.btn()} onClick={() => setDepositModal(false)}>Cancel</button>
              <button
                style={st.btn("primary")}
                onClick={async () => {
                  const dep = deposit;
                  setDepositModal(false);
                  await doPushToEstimate(dep);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}
